/** Every editor-backed Google type carries this prefix; the bytes live in Google, not in Drive. */
const GOOGLE_APPS_MIME_PREFIX = "application/vnd.google-apps.";
/**
 * The three Google editor types the pipeline can read, and the Office format each is exported
 * as. OOXML is what the document worker already parses, so an exported Google file enters the
 * pipeline as an ordinary `.docx`/`.xlsx`/`.pptx`.
 *
 * The export is the pipeline's business and never the reader's: a person on Google Workspace
 * picked a Google Doc and owns a Google Doc. So each entry names the document twice — in a
 * sentence (`label`) and in a badge (`shortLabel`) — and the Office format it is converted into
 * stays where it belongs, on the bytes.
 */
export const GOOGLE_DOCUMENT_MIME_TYPES = {
    "application/vnd.google-apps.document": {
        exportMimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        extension: ".docx",
        label: "Google Docs",
        shortLabel: "G Doc",
    },
    "application/vnd.google-apps.spreadsheet": {
        exportMimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        extension: ".xlsx",
        label: "Google Sheets",
        shortLabel: "G Sheet",
    },
    "application/vnd.google-apps.presentation": {
        exportMimeType: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        extension: ".pptx",
        label: "Google Slides",
        shortLabel: "G Slides",
    },
};
/** The Google types an import names in its refusal, plural so one sentence fits them all. */
export const GOOGLE_REFUSED_MIME_TYPES = {
    "application/vnd.google-apps.form": "Google Forms",
    "application/vnd.google-apps.drawing": "Google Drawings",
    "application/vnd.google-apps.site": "Google Sites",
    "application/vnd.google-apps.map": "Google My Maps",
    "application/vnd.google-apps.jamboard": "Jamboard boards",
    "application/vnd.google-apps.shortcut": "Drive shortcuts",
    "application/vnd.google-apps.folder": "Drive folders",
    "application/vnd.google-apps.script": "Apps Script files",
};
/**
 * How a Drive file becomes bytes: exported for the three editor types, downloaded for anything
 * stored in Drive as a file already, refused for every other Google type.
 *
 * A Google type nobody has listed is refused rather than downloaded — `files.get?alt=media`
 * answers an editor-backed file with an error, and refusing names the type in the sentence.
 */
export function resolveGoogleExport(mimeType) {
    const mime = normalizeMimeType(mimeType);
    const exportFormat = resolveGoogleEditorFormat(mime);
    if (exportFormat !== undefined) {
        return {
            kind: "export",
            exportMimeType: exportFormat.exportMimeType,
            extension: exportFormat.extension,
            label: exportFormat.label,
        };
    }
    const refusedLabel = GOOGLE_REFUSED_MIME_TYPES[mime];
    if (refusedLabel !== undefined)
        return { kind: "refused", label: refusedLabel };
    if (mime.startsWith(GOOGLE_APPS_MIME_PREFIX)) {
        return { kind: "refused", label: `Google file type ${mime}` };
    }
    return { kind: "download" };
}
/**
 * Which editor owns a document, from the type the provider reports for it.
 *
 * The one test for "these bytes are an export, not the document" — the fact that decides the
 * type badge, the download's wording and whether a new name is held to the stored extension.
 * `undefined` for an upload and for a file Drive merely stored (a PDF picked from Drive is a
 * PDF), which is why the argument is optional: a row that carries no provider type has no
 * editor behind it.
 */
export function resolveGoogleEditorFormat(googleMimeType) {
    if (!googleMimeType)
        return undefined;
    return GOOGLE_DOCUMENT_MIME_TYPES[normalizeMimeType(googleMimeType)];
}
/** A content type as the table is keyed: no parameters, no casing, no surrounding space. */
function normalizeMimeType(mimeType) {
    return mimeType.split(";")[0]?.trim().toLowerCase() ?? "";
}
/**
 * The exported file's name. Drive reports a Google file's name without an extension, and the
 * parser is routed by the stored name, so the export format has to be spelled in it.
 *
 * This is the *file* name and never the document's: the name a person reads stays the one Drive
 * gave, so a registration keeps the two apart (`artifacts.name` / `artifacts.displayName`)
 * rather than showing a Google Doc as a `.docx`.
 */
export function withGoogleExportExtension(name, extension) {
    return name.toLowerCase().endsWith(extension.toLowerCase()) ? name : `${name}${extension}`;
}
/** The one user-facing sentence for a Google file type the import cannot read. */
export function describeUnsupportedGoogleFileType(label) {
    return `${label} cannot be imported from Google Drive`;
}
/**
 * Drive refuses to export a Google file whose exported content exceeds this, whatever the
 * source document's own size, and answers `exportSizeLimitExceeded` rather than bytes.
 */
export const GOOGLE_DRIVE_EXPORT_LIMIT_BYTES = 10 * 1024 * 1024;
